import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { instagramCampaignService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  Card, Badge, Button, Modal, Input, Field, Select, Textarea, EmptyState, Spinner,
} from '../../components/ui';

function buildExportScript(username, password) {
  // JSON.stringify's escaping (quotes, backslashes, \n, \t, \uXXXX) is a
  // valid Python double-quoted string literal too, so this is safe to
  // embed directly without a templating step on the backend.
  const usernameLiteral = JSON.stringify(username);
  const passwordLiteral = JSON.stringify(password);
  return `"""Auto-generated by the Instagram Campaigns admin page for @${username}.
Run this on YOUR OWN computer - not the server - to log into Instagram
from your real (residential/mobile) IP address and export a session that
the admin panel can import directly.

Why: logging in from the server's IP is what triggers Instagram's
"checkpoint_challenge_required" security check, which sometimes cannot be
resolved automatically at all - the account owner has to approve it in the
real Instagram app or on instagram.com. Logging in once from here instead
sidesteps that: the session this produces gets reused by the server
afterward without it ever having to log in itself.

Usage:
    pip install instagrapi
    python ${username}_export_session.py

It writes instagram_session.json in the current folder. Open that file,
copy its entire contents, and paste them into the "Import session" field
on the Instagram Campaigns admin page, along with this username.

If Instagram shows an unresolvable checkpoint even from here, open the
Instagram app or instagram.com yourself, log in normally, complete
whatever security prompt appears there, then run this script again.
"""
import json
import sys

from instagrapi import Client
from instagrapi.exceptions import ChallengeRequired, TwoFactorRequired

USERNAME = ${usernameLiteral}
PASSWORD = ${passwordLiteral}


def main():
    client = Client()
    try:
        client.login(USERNAME, PASSWORD)
    except TwoFactorRequired:
        code = input('Enter the 2FA code from your authenticator app/SMS: ').strip()
        client.login(USERNAME, PASSWORD, verification_code=code)
    except ChallengeRequired:
        print()
        print('Instagram is asking for verification that could not be completed automatically here.')
        print('Open the Instagram app or instagram.com, log in normally, and complete any')
        print('security prompt shown there, then run this script again.')
        sys.exit(1)

    settings = client.get_settings()
    with open('instagram_session.json', 'w') as f:
        json.dump(settings, f, indent=2)

    print()
    print(f'Logged in as @{USERNAME} (user id {client.user_id}).')
    print('Wrote instagram_session.json in this folder.')
    print('Copy its full contents into the "Import session" field in the admin panel.')


if __name__ == '__main__':
    main()
`;
}

function RiskNotice() {
  const { t } = useTranslation();
  return (
    <Card className="bg-amber-50 border-amber-200">
      <div className="flex gap-3">
        <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-amber-800">
          {t("This connects using Instagram's unofficial private API (username & password), not Meta's official platform. Instagram can flag, challenge, or ban an account used this way at any time, and this can stop working without notice if Instagram changes their app. Consider connecting a secondary/test account rather than your main one.")}
        </p>
      </div>
    </Card>
  );
}

function HelpGuide({ open, onToggle }) {
  const { t } = useTranslation();
  return (
    <Card>
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between text-left">
        <div>
          <h3 className="font-semibold text-gray-900">{t('How to connect an Instagram account')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('Read this if "Connect" fails with a checkpoint error.')}</p>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <ol className="mt-4 space-y-3 text-sm text-gray-700 list-decimal list-inside">
          <li>
            <span className="font-medium text-gray-900">{t('Try logging in directly first')}</span>
            <p className="text-gray-500 mt-0.5 ml-5">
              {t('Click "Connect Account" → "Log in with password" and enter the Instagram username and password. This works immediately for most accounts.')}
            </p>
          </li>
          <li>
            <span className="font-medium text-gray-900">{t('If it fails with a checkpoint/verification error')}</span>
            <p className="text-gray-500 mt-0.5 ml-5">
              {t("Instagram sometimes blocks a login from this server's address with a check it won't let us complete automatically. This isn't a bug - it just means the login itself has to happen from a normal home/mobile connection instead.")}
            </p>
          </li>
          <li>
            <span className="font-medium text-gray-900">{t('Download the setup script')}</span>
            <p className="text-gray-500 mt-0.5 ml-5">
              {t('With the username and password still filled in on the password tab, click "Download Script" - it saves a small Python file with those credentials already in it, ready to run.')}
            </p>
          </li>
          <li>
            <span className="font-medium text-gray-900">{t('Run it on your own computer')}</span>
            <p className="text-gray-500 mt-0.5 ml-5">{t('Not this server - your own laptop/desktop, on your normal home or mobile internet connection:')}</p>
            <code className="block ml-5 mt-1 text-xs bg-gray-50 rounded px-2 py-1 text-gray-700 break-all">
              pip install instagrapi
            </code>
            <code className="block ml-5 mt-1 text-xs bg-gray-50 rounded px-2 py-1 text-gray-700 break-all">
              python the_downloaded_script.py
            </code>
            <p className="text-gray-500 mt-1 ml-5">
              {t('It creates a file called instagram_session.json in the same folder. If Instagram still shows an unresolvable checkpoint even here, open the Instagram app yourself, log in normally, complete whatever prompt appears, then run the script again.')}
            </p>
          </li>
          <li>
            <span className="font-medium text-gray-900">{t('Import the session here')}</span>
            <p className="text-gray-500 mt-0.5 ml-5">
              {t('Back on this page: "Connect Account" → "Import session" tab → paste the username and the full contents of instagram_session.json → Import. The server will reuse this session going forward instead of logging in itself.')}
            </p>
          </li>
        </ol>
      )}
    </Card>
  );
}

const EMPTY_FORM = {
  account: '', name: '', media_id: '', permalink: '', caption_preview: '',
  keywords: '', opening_message: '', follower_message: '', non_follower_message: '', status: 'draft',
};

const LOG_STATUS_TONE = {
  matched: 'blue',
  awaiting_follow: 'yellow',
  follower_message_sent: 'green',
  failed: 'red',
};

function AdminInstagramCampaigns() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();

  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [mediaOptions, setMediaOptions] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const [connecting, setConnecting] = useState(false);
  const [connectMode, setConnectMode] = useState('password'); // 'password' | 'session'
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [challenge, setChallenge] = useState(null); // { pendingId, challengeType }
  const [code, setCode] = useState('');
  const [connectSubmitting, setConnectSubmitting] = useState(false);
  const [sessionForm, setSessionForm] = useState({ username: '', sessionData: '' });
  const [guideOpen, setGuideOpen] = useState(true);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([instagramCampaignService.getAccounts(), instagramCampaignService.getCampaigns()])
      .then(([accRes, campRes]) => {
        setAccounts(accRes.data.results || accRes.data || []);
        setCampaigns(campRes.data.results || campRes.data || []);
      })
      .catch((e) => addNotification(getApiErrorMessage(e, t, t('Failed to load Instagram campaigns')), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetConnectState = () => {
    setConnecting(false);
    setConnectMode('password');
    setLoginForm({ username: '', password: '' });
    setChallenge(null);
    setCode('');
    setSessionForm({ username: '', sessionData: '' });
  };

  const submitLogin = async () => {
    if (!loginForm.username || !loginForm.password) {
      addNotification(t('Enter both username and password'), 'error');
      return;
    }
    setConnectSubmitting(true);
    try {
      const res = await instagramCampaignService.login(loginForm.username, loginForm.password);
      if (res.data.challenge_required) {
        setChallenge({ pendingId: res.data.pending_id, challengeType: res.data.challenge_type });
        addNotification(t('Instagram sent a verification code — enter it below'), 'success');
      } else {
        addNotification(t('Instagram account connected'), 'success');
        resetConnectState();
        fetchAll();
      }
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to connect Instagram account')), 'error');
    } finally {
      setConnectSubmitting(false);
    }
  };

  const submitChallenge = async () => {
    if (!code.trim()) {
      addNotification(t('Enter the verification code'), 'error');
      return;
    }
    setConnectSubmitting(true);
    try {
      await instagramCampaignService.verifyChallenge(challenge.pendingId, code.trim());
      addNotification(t('Instagram account connected'), 'success');
      resetConnectState();
      fetchAll();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Verification failed')), 'error');
    } finally {
      setConnectSubmitting(false);
    }
  };

  const downloadScript = () => {
    if (!loginForm.username || !loginForm.password) {
      addNotification(t('Enter both username and password first'), 'error');
      return;
    }
    const content = buildExportScript(loginForm.username, loginForm.password);
    const blob = new Blob([content], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${loginForm.username}_export_session.py`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const submitSession = async () => {
    if (!sessionForm.username || !sessionForm.sessionData.trim()) {
      addNotification(t('Enter both the username and the exported session'), 'error');
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(sessionForm.sessionData);
    } catch (e) {
      addNotification(t('That does not look like valid JSON — paste the full contents of instagram_session.json'), 'error');
      return;
    }
    setConnectSubmitting(true);
    try {
      await instagramCampaignService.importSession(sessionForm.username, parsed);
      addNotification(t('Instagram account connected'), 'success');
      resetConnectState();
      fetchAll();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to import session')), 'error');
    } finally {
      setConnectSubmitting(false);
    }
  };

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, account: accounts[0]?.id || '' });
    setEditingId(null);
    setMediaOptions([]);
    setModalOpen(true);
    if (accounts[0]?.id) loadMediaOptions(accounts[0].id);
  };

  const openEdit = (campaign) => {
    setForm({ ...campaign, keywords: (campaign.keywords || []).join(', ') });
    setEditingId(campaign.id);
    setMediaOptions([]);
    setModalOpen(true);
    if (campaign.account) loadMediaOptions(campaign.account);
  };

  const loadMediaOptions = async (accountId) => {
    if (!accountId) { setMediaOptions([]); return; }
    setMediaLoading(true);
    try {
      const res = await instagramCampaignService.getMediaOptions(accountId);
      setMediaOptions(res.data || []);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to load recent posts')), 'error');
    } finally {
      setMediaLoading(false);
    }
  };

  const onAccountChange = (accountId) => {
    setForm((prev) => ({ ...prev, account: accountId, media_id: '', permalink: '', caption_preview: '' }));
    loadMediaOptions(accountId);
  };

  const pickMedia = (media) => {
    setForm((prev) => ({
      ...prev,
      media_id: media.id,
      permalink: media.permalink || '',
      caption_preview: (media.caption || '').slice(0, 500),
    }));
  };

  const save = async () => {
    if (!form.account || !form.name || !form.media_id || !form.opening_message || !form.keywords) {
      addNotification(t('Fill in account, name, a selected post, keywords, and the opening message'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean),
      };
      if (editingId) {
        await instagramCampaignService.updateCampaign(editingId, payload);
        addNotification(t('Campaign updated'), 'success');
      } else {
        await instagramCampaignService.createCampaign(payload);
        addNotification(t('Campaign created'), 'success');
      }
      setModalOpen(false);
      fetchAll();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to save campaign')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCampaign = async (campaign) => {
    if (!(await confirm(t('Delete campaign "{{name}}"?', { name: campaign.name })))) return;
    try {
      await instagramCampaignService.deleteCampaign(campaign.id);
      addNotification(t('Campaign deleted'), 'success');
      fetchAll();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to delete campaign')), 'error');
    }
  };

  const runCampaign = async (campaign) => {
    setRunningId(campaign.id);
    try {
      const res = await instagramCampaignService.runCampaign(campaign.id);
      setRunResult({ campaignName: campaign.name, ...res.data });
      addNotification(t('Run complete: {{sent}} messaged, {{errors}} errors', {
        sent: res.data.run.opening_messages_sent, errors: res.data.run.errors,
      }), res.data.run.errors > 0 ? 'error' : 'success');
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Campaign run failed')), 'error');
    } finally {
      setRunningId(null);
    }
  };

  const accountLabel = (id) => accounts.find((a) => a.id === id)?.username || '—';

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav
          title={t('Instagram Campaigns')}
          subtitle={t('Automatically DM commenters who use a keyword on your posts')}
        />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <RiskNotice />
          <HelpGuide open={guideOpen} onToggle={() => setGuideOpen((v) => !v)} />

          <Card>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-semibold text-gray-900">{t('Connected Accounts')}</h3>
                {accounts.length === 0 ? (
                  <p className="text-sm text-gray-500 mt-1">{t('Connect an Instagram account to get started.')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {accounts.map((a) => (
                      <Badge key={a.id} tone={a.is_active ? 'green' : 'gray'}>@{a.username}</Badge>
                    ))}
                  </div>
                )}
              </div>
              {!connecting && <Button onClick={() => setConnecting(true)}>+ {t('Connect Account')}</Button>}
            </div>

            {connecting && (
              <div className="mt-4 pt-4 border-t border-gray-100 max-w-md space-y-3">
                {!challenge && (
                  <div className="flex gap-4 text-sm border-b border-gray-100 pb-2">
                    <button
                      type="button"
                      onClick={() => setConnectMode('password')}
                      className={connectMode === 'password' ? 'font-semibold text-primary-600' : 'text-gray-500'}
                    >
                      {t('Log in with password')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConnectMode('session')}
                      className={connectMode === 'session' ? 'font-semibold text-primary-600' : 'text-gray-500'}
                    >
                      {t('Import session (recommended if login fails)')}
                    </button>
                  </div>
                )}

                {challenge ? (
                  <>
                    <p className="text-sm text-gray-600">
                      {t('Instagram sent a code by {{type}} to verify this login.', {
                        type: challenge.challengeType === 'sms' ? t('SMS') : t('email'),
                      })}
                    </p>
                    <Field label={t('Verification Code')}>
                      <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
                    </Field>
                    <div className="flex gap-2">
                      <Button loading={connectSubmitting} onClick={submitChallenge}>{t('Verify')}</Button>
                      <Button variant="secondary" onClick={resetConnectState}>{t('Cancel')}</Button>
                    </div>
                  </>
                ) : connectMode === 'password' ? (
                  <>
                    <Field label={t('Instagram Username')}>
                      <Input
                        value={loginForm.username}
                        onChange={(e) => setLoginForm((prev) => ({ ...prev, username: e.target.value }))}
                        placeholder="school_account"
                      />
                    </Field>
                    <Field label={t('Instagram Password')}>
                      <Input
                        type="password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
                      />
                    </Field>
                    <p className="text-xs text-gray-400">
                      {t('If Instagram blocks this with a checkpoint that never sends a code, our server\'s IP is likely the cause — click "Download Script" below, run it on your own computer, then switch to "Import session".')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button loading={connectSubmitting} onClick={submitLogin}>{t('Connect')}</Button>
                      <Button variant="secondary" onClick={downloadScript}>{t('Download Script')}</Button>
                      <Button variant="secondary" onClick={resetConnectState}>{t('Cancel')}</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-500">
                      {t('Run the export script on your own computer (not this server) to log in from your real IP, then paste the contents of the instagram_session.json it creates below.')}
                    </p>
                    <Field label={t('Instagram Username')}>
                      <Input
                        value={sessionForm.username}
                        onChange={(e) => setSessionForm((prev) => ({ ...prev, username: e.target.value }))}
                        placeholder="school_account"
                      />
                    </Field>
                    <Field label={t('Exported Session (instagram_session.json contents)')}>
                      <Textarea
                        rows={6}
                        value={sessionForm.sessionData}
                        onChange={(e) => setSessionForm((prev) => ({ ...prev, sessionData: e.target.value }))}
                        placeholder='{"uuids": {...}, "cookies": {...}, ...}'
                        className="font-mono text-xs"
                      />
                    </Field>
                    <div className="flex gap-2">
                      <Button loading={connectSubmitting} onClick={submitSession}>{t('Import')}</Button>
                      <Button variant="secondary" onClick={resetConnectState}>{t('Cancel')}</Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{t('Campaigns')}</h3>
                <Button onClick={openCreate} disabled={accounts.length === 0}>+ {t('New Campaign')}</Button>
              </div>

              {campaigns.length === 0 ? (
                <Card>
                  <EmptyState
                    title={t('No campaigns yet')}
                    description={t('Create a campaign against one of your recent posts and a list of trigger keywords.')}
                    action={accounts.length > 0 && <Button onClick={openCreate}>+ {t('New Campaign')}</Button>}
                  />
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {campaigns.map((c) => (
                    <Card key={c.id}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-500">@{c.account_username || accountLabel(c.account)}</p>
                        </div>
                        <Badge status={c.status} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {(c.keywords || []).map((kw) => (
                          <span key={kw} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{kw}</span>
                        ))}
                      </div>
                      {c.permalink && (
                        <a href={c.permalink} target="_blank" rel="noreferrer" className="text-xs text-primary-600 mt-2 inline-block truncate">
                          {c.caption_preview || c.permalink}
                        </a>
                      )}
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" loading={runningId === c.id} onClick={() => runCampaign(c)}>{t('Run')}</Button>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(c)}>{t('Edit')}</Button>
                        <Button size="sm" variant="danger" onClick={() => deleteCampaign(c)}>{t('Delete')}</Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {runResult && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{t('Last run: {{name}}', { name: runResult.campaignName })}</h3>
                <button onClick={() => setRunResult(null)} className="text-gray-400 hover:text-gray-600 text-sm">{t('Dismiss')}</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4 text-center">
                {[
                  ['comments_fetched', t('Fetched')],
                  ['comments_matched', t('Matched')],
                  ['opening_messages_sent', t('Messaged')],
                  ['non_follower_messages_sent', t('Asked to Follow')],
                  ['follower_messages_sent', t('Sent Follower Msg')],
                  ['follows_detected', t('Followed Since Last Run')],
                ].map(([key, label]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-lg font-display font-bold text-gray-900">{runResult.run[key]}</p>
                    <p className="text-xs text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
              {runResult.run.errors > 0 && (
                <p className="text-xs text-red-500 mb-3">{t('{{count}} errors this run — see rows below.', { count: runResult.run.errors })}</p>
              )}
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {runResult.logs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-2.5 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">@{log.commenter_username || log.commenter_ig_scoped_id}</p>
                      <p className="text-xs text-gray-500 truncate">{log.comment_text}</p>
                      {log.error_message && <p className="text-xs text-red-500 truncate">{log.error_message}</p>}
                    </div>
                    <Badge tone={LOG_STATUS_TONE[log.status] || 'gray'}>{t(log.status)}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? t('Edit Campaign') : t('New Campaign')}
        size="lg"
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Instagram Account')} required>
            <Select value={form.account} onChange={(e) => onAccountChange(e.target.value)}>
              <option value="">{t('Select an account')}</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>@{a.username}</option>)}
            </Select>
          </Field>
          <Field label={t('Campaign Name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('e.g. "Back to school promo"')} />
          </Field>

          <Field label={t('Post')} required hint={t('Pick a recent post from this account.')}>
            {mediaLoading ? (
              <div className="p-4 flex justify-center"><Spinner /></div>
            ) : mediaOptions.length === 0 ? (
              <p className="text-xs text-gray-400">{form.account ? t('No recent posts found.') : t('Select an account first.')}</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                {mediaOptions.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => pickMedia(m)}
                    className={`relative rounded-lg overflow-hidden border-2 ${form.media_id === m.id ? 'border-primary-500' : 'border-transparent'}`}
                  >
                    <img
                      src={m.thumbnail_url}
                      alt={m.caption || ''}
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            {form.media_id && <p className="text-xs text-gray-500 mt-2 truncate">{t('Selected')}: {form.caption_preview || form.media_id}</p>}
          </Field>

          <Field label={t('Trigger Keywords')} required hint={t('Comma-separated. A comment matches if it contains any of these words.')}>
            <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder={t('e.g. price, info, link')} />
          </Field>
          <Field label={t('Opening Message')} required hint={t('Sent to every matching commenter, before the follow check below.')}>
            <Textarea rows={3} value={form.opening_message} onChange={(e) => setForm({ ...form, opening_message: e.target.value })} />
          </Field>
          <Field label={t('Message for Followers')} hint={t('Sent right after, if they already follow the account. Can include a link.')}>
            <Textarea rows={3} value={form.follower_message} onChange={(e) => setForm({ ...form, follower_message: e.target.value })} />
          </Field>
          <Field label={t('Message for Non-Followers')} hint={t("Sent instead, asking them to follow. Once a later Run detects they've followed, the Followers message above is sent automatically.")}>
            <Textarea rows={3} value={form.non_follower_message} onChange={(e) => setForm({ ...form, non_follower_message: e.target.value })} />
          </Field>
          <Field label={t('Status')}>
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="draft">{t('Draft')}</option>
              <option value="active">{t('Active')}</option>
              <option value="paused">{t('Paused')}</option>
            </Select>
          </Field>
        </div>
      </Modal>
    </div>
  );
}

export default AdminInstagramCampaigns;
